import * as React from 'react'
import { CircleIcon } from 'lucide-react'
import { CheckIcon, ChevronRightIcon } from '@/src/icons'
import { Menu as MenuPrimitive } from '@base-ui/react/menu'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// DropdownMenu — migrated from Radix to Base UI
// ---------------------------------------------------------------------------
// Structural changes:
//   Radix: Root > Portal > Content (with positioning)
//   Base UI: Root > Portal > Positioner > Popup (positioning on Positioner)
//
// Sub-component renames:
//   Content     → Positioner + Popup (baked into DropdownMenuContent wrapper)
//   Label       → GroupLabel
//   Sub         → SubmenuRoot
//   SubTrigger  → SubmenuTrigger
//   SubContent  → Portal > Positioner > Popup (baked into DropdownMenuSubContent)
//   ItemIndicator → CheckboxItemIndicator / RadioItemIndicator
//
// CSS variable renames:
//   --radix-dropdown-menu-content-available-height → --available-height
//   --radix-dropdown-menu-content-transform-origin → --transform-origin
//
// Data attribute changes:
//   data-[state=open/closed] → data-[open] / data-[starting-style] / data-[ending-style]
//   data-[state=open] on SubTrigger → data-[popup-open]
//   data-[disabled] → data-[disabled] (same)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DropdownMenu (Root)
// ---------------------------------------------------------------------------

function DropdownMenu({
  onOpenChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Root>, 'onOpenChange'> & {
  onOpenChange?: (open: boolean) => void
}) {
  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      onOpenChange?.(open)
    },
    [onOpenChange],
  )

  return (
    <MenuPrimitive.Root
      data-slot="dropdown-menu"
      onOpenChange={onOpenChange ? handleOpenChange : undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuPortal
// ---------------------------------------------------------------------------

function DropdownMenuPortal({ ...props }: React.ComponentProps<typeof MenuPrimitive.Portal>) {
  return <MenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
}

// ---------------------------------------------------------------------------
// DropdownMenuTrigger
// ---------------------------------------------------------------------------

function DropdownMenuTrigger({
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Trigger>, 'render'> & {
  asChild?: boolean
  children?: React.ReactNode
}) {
  // Base UI Trigger accepts children directly; the `render` prop is not needed
  // for simple use. If asChild is true, the single child IS the trigger element.
  // Base UI natively supports render-as-child via `render` prop, but we keep
  // the `asChild` API for backwards compatibility.
  if (asChild) {
    const child = React.Children.only(children)
    if (React.isValidElement(child)) {
      return <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" render={child as React.ReactElement} {...props} />
    }
  }
  return (
    <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props}>
      {children}
    </MenuPrimitive.Trigger>
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuContent
// ---------------------------------------------------------------------------
// Bakes in Portal > Positioner > Popup so consumers keep the same simple API.

function DropdownMenuContent({
  className,
  // DS: floating surfaces use an 8 px gap from their trigger.
  sideOffset = 8,
  side = 'bottom',
  align = 'start',
  forceMount,
  children,
  ...props
}: {
  className?: string
  sideOffset?: number
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
  /** Radix compat — keep the popup mounted in the DOM while hidden */
  forceMount?: boolean
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof MenuPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <MenuPrimitive.Portal keepMounted={forceMount}>
      <MenuPrimitive.Positioner side={side} sideOffset={sideOffset} align={align}>
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            // DS Menu: bg-popover surface, --border, radius-lg (8 px), shadow-md.
            // Base UI: --available-height, --transform-origin replace Radix CSS vars
            'z-50 max-h-[var(--available-height)] min-w-[8rem] origin-[var(--transform-origin)] overflow-x-hidden overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-md outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuGroup
// ---------------------------------------------------------------------------

function DropdownMenuGroup({ ...props }: React.ComponentProps<typeof MenuPrimitive.Group>) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

// ---------------------------------------------------------------------------
// DropdownMenuItem
// ---------------------------------------------------------------------------

function DropdownMenuItem({
  className,
  inset,
  variant = 'default',
  onSelect,
  asChild,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.Item>, 'render'> & {
  inset?: boolean
  variant?: 'default' | 'destructive'
  /** Radix compat — called when the item is selected */
  onSelect?: () => void
  /** Radix compat — render as child element */
  asChild?: boolean
  children?: React.ReactNode
}) {
  // Build the render prop if asChild is true
  const renderProps: { render?: React.ReactElement } = {}
  if (asChild && children) {
    const child = React.Children.only(children)
    if (React.isValidElement(child)) {
      renderProps.render = child as React.ReactElement
    }
  }

  return (
    // DS Menu item: text-sm, py-2 (8 px top/bottom) for comfortable touch target,
    // icons inline at size-4 (icon-sm = 16 px). Hover/focus uses bg-accent.
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      onClick={onSelect}
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 data-[variant=destructive]:text-destructive data-[variant=destructive]:data-[highlighted]:bg-destructive/10 data-[variant=destructive]:data-[highlighted]:text-destructive dark:data-[variant=destructive]:data-[highlighted]:bg-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground data-[variant=destructive]:*:[svg]:text-destructive!",
        className,
      )}
      {...renderProps}
      {...props}
    >
      {asChild ? undefined : children}
    </MenuPrimitive.Item>
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuCheckboxItem
// ---------------------------------------------------------------------------

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  onCheckedChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.CheckboxItem>, 'render' | 'onCheckedChange'> & {
  children?: React.ReactNode
  onCheckedChange?: (checked: boolean) => void
}) {
  const handleCheckedChange = React.useCallback(
    (newChecked: boolean) => {
      onCheckedChange?.(newChecked)
    },
    [onCheckedChange],
  )

  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      checked={checked}
      onCheckedChange={onCheckedChange ? handleCheckedChange : undefined}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <MenuPrimitive.CheckboxItemIndicator keepMounted={false}>
          <CheckIcon className="size-4" />
        </MenuPrimitive.CheckboxItemIndicator>
      </span>
      {children}
    </MenuPrimitive.CheckboxItem>
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuRadioGroup
// ---------------------------------------------------------------------------

function DropdownMenuRadioGroup({
  onValueChange,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.RadioGroup>, 'onValueChange'> & {
  onValueChange?: (value: string) => void
}) {
  const handleValueChange = React.useCallback(
    (value: unknown) => {
      onValueChange?.(String(value ?? ''))
    },
    [onValueChange],
  )

  return (
    <MenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      onValueChange={onValueChange ? handleValueChange : undefined}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuRadioItem
// ---------------------------------------------------------------------------

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.RadioItem>, 'render'> & {
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm py-2 pr-2 pl-8 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <span className="pointer-events-none absolute left-2 flex size-3.5 items-center justify-center">
        <MenuPrimitive.RadioItemIndicator keepMounted={false}>
          <CircleIcon className="size-2 fill-current" />
        </MenuPrimitive.RadioItemIndicator>
      </span>
      {children}
    </MenuPrimitive.RadioItem>
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuLabel
// ---------------------------------------------------------------------------

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<'div'> & {
  inset?: boolean
}) {
  // Base UI's GroupLabel requires a parent Menu.Group context.
  // Radix allowed standalone labels outside groups. We use a plain div
  // to preserve the existing API while avoiding the context error.
  return (
    <div
      role="group"
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn('px-2 py-1.5 text-sm font-medium data-[inset]:pl-8', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuSeparator
// ---------------------------------------------------------------------------

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  // Base UI's Separator is a generic component. We use a simple div with role
  // to match the expected menu separator semantics.
  return (
    <div
      role="separator"
      data-slot="dropdown-menu-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuShortcut
// ---------------------------------------------------------------------------

function DropdownMenuShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuSub (SubmenuRoot)
// ---------------------------------------------------------------------------

function DropdownMenuSub({
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.SubmenuRoot>, 'children'> & {
  children?: React.ReactNode
}) {
  return <MenuPrimitive.SubmenuRoot data-slot="dropdown-menu-sub" {...props} />
}

// ---------------------------------------------------------------------------
// DropdownMenuSubTrigger
// ---------------------------------------------------------------------------

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: Omit<React.ComponentProps<typeof MenuPrimitive.SubmenuTrigger>, 'render'> & {
  inset?: boolean
  children?: React.ReactNode
}) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "flex cursor-default items-center gap-2 rounded-sm px-2 py-2 text-sm outline-hidden select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[inset]:pl-8 data-[popup-open]:bg-accent data-[popup-open]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="ml-auto size-4" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

// ---------------------------------------------------------------------------
// DropdownMenuSubContent
// ---------------------------------------------------------------------------
// Bakes in Portal > Positioner > Popup for the sub-menu.

function DropdownMenuSubContent({
  className,
  children,
  ...props
}: {
  className?: string
  children?: React.ReactNode
} & Omit<React.ComponentProps<typeof MenuPrimitive.Popup>, 'className' | 'children'>) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Positioner>
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-sub-content"
          className={cn(
            // DS: same surface tokens as the root menu content.
            // Base UI: --transform-origin replaces --radix-dropdown-menu-content-transform-origin
            'z-50 min-w-[8rem] origin-[var(--transform-origin)] overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-lg outline-hidden transition-[opacity,scale] duration-150 scale-100 opacity-100 data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[ending-style]:scale-95 data-[ending-style]:opacity-0',
            className,
          )}
          {...props}
        >
          {children}
        </MenuPrimitive.Popup>
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
